import { useState, type FormEvent } from "react";
import type { CrmRevenue, CrmRevenueKind, CrmRevenueStatus } from "@shared/modules/crm";
import { api } from "../../../core/lib/api";
import { Button } from "../../../core/components/ui/button";
import { Input, Select } from "../../../core/components/ui/input";
import { formatDateTime } from "../../../core/lib/format";
import { useToastStore } from "../../../core/stores/toast";
import {
  CRM_REVENUE_KIND_LABELS,
  CRM_REVENUE_STATUS_LABELS,
  centsToInput,
  dateTimeLocalToIso,
  formatCents,
  parseAmountToCents,
  toDateTimeLocalValue,
} from "../lib/crm";
import { LabelOptions } from "./label-options";

interface Draft {
  kind: CrmRevenueKind;
  label: string;
  periodStart: string;
  periodEnd: string;
  amount: string;
  commissionPct: string;
  status: CrmRevenueStatus;
  invoiceRef: string;
  paidAt: string;
  notes: string;
}

const EMPTY: Draft = {
  kind: "abonnement",
  label: "",
  periodStart: "",
  periodEnd: "",
  amount: "",
  commissionPct: "",
  status: "prevu",
  invoiceRef: "",
  paidAt: "",
  notes: "",
};

// Les périodes sont saisies sans heure : on ancre à midi local pour qu'une
// conversion en UTC ne fasse pas glisser la ligne sur le mois précédent.
function isoFromDate(value: string): string | null {
  return dateTimeLocalToIso(value ? `${value}T12:00` : "");
}

interface RevenueListProps {
  accountId: string;
  items: CrmRevenue[];
  currency: string;
  /** Taux du dossier, appliqué quand la ligne ne porte pas son propre pourcentage. */
  commissionPct: number;
  onChanged: () => void;
}

export function RevenueList({
  accountId,
  items,
  currency,
  commissionPct,
  onChanged,
}: RevenueListProps) {
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const base = `/api/crm/accounts/${accountId}/revenues`;

  const reset = () => {
    setDraft(EMPTY);
    setEditingId(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft.label.trim() || saving) return;
    const periodStart = isoFromDate(draft.periodStart);
    if (!periodStart) {
      push("danger", "Début de période requis");
      return;
    }
    const amountCents = parseAmountToCents(draft.amount);
    if (amountCents === null) {
      push(
        "danger",
        "Montant invalide",
        "Saisissez un montant en dollars (ex : 149,00 ou -50 pour un ajustement).",
      );
      return;
    }
    const payload = {
      kind: draft.kind,
      label: draft.label.trim(),
      periodStart,
      periodEnd: isoFromDate(draft.periodEnd),
      amountCents,
      commissionPct: draft.commissionPct.trim()
        ? Math.min(100, Math.max(0, Number(draft.commissionPct)))
        : null,
      status: draft.status,
      invoiceRef: draft.invoiceRef.trim() || null,
      paidAt: isoFromDate(draft.paidAt),
      notes: draft.notes.trim() || null,
    };
    setSaving(true);
    try {
      await api(editingId ? `${base}/${editingId}` : base, {
        method: editingId ? "PATCH" : "POST",
        // La devise n'est posée qu'à la création : une ligne importée dans une
        // autre devise ne doit pas basculer sur celle du dossier à chaque édition.
        body: JSON.stringify(editingId ? payload : { ...payload, currency }),
      });
      push("success", editingId ? "Ligne de revenu mise à jour" : "Ligne de revenu ajoutée");
      reset();
      onChanged();
    } catch {
      push("danger", "Échec de l'enregistrement de la ligne");
    } finally {
      setSaving(false);
    }
  };

  const edit = (r: CrmRevenue) => {
    setEditingId(r.id);
    setDraft({
      kind: r.kind,
      label: r.label,
      periodStart: toDateTimeLocalValue(r.periodStart).slice(0, 10),
      periodEnd: toDateTimeLocalValue(r.periodEnd).slice(0, 10),
      amount: centsToInput(r.amountCents),
      commissionPct: r.commissionPct === null ? "" : String(r.commissionPct),
      status: r.status,
      invoiceRef: r.invoiceRef ?? "",
      paidAt: toDateTimeLocalValue(r.paidAt).slice(0, 10),
      notes: r.notes ?? "",
    });
  };

  const changeStatus = async (id: string, status: CrmRevenueStatus) => {
    try {
      await api(`${base}/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      onChanged();
    } catch {
      push("danger", "Échec du changement de statut");
    }
  };

  const remove = async (id: string) => {
    try {
      await api(`${base}/${id}`, { method: "DELETE" });
      if (editingId === id) reset();
      push("success", "Ligne de revenu supprimée");
      onChanged();
    } catch {
      push("danger", "Échec de la suppression");
    }
  };

  const collectedCents = items
    .filter((r) => r.status === "encaisse")
    .reduce((sum, r) => sum + r.amountCents, 0);

  return (
    <section className="crm-section">
      <div className="crm-section__head">
        <h2 className="crm-section__title">Revenus & commissions</h2>
        <div className="crm-section__actions">
          <span className="crm-chip crm-chip--muted">
            Encaissé {formatCents(collectedCents, currency)}
          </span>
        </div>
      </div>

      <form className="crm-form crm-form--inline" onSubmit={submit}>
        <Select
          aria-label="Nature de la ligne"
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value as CrmRevenueKind })}
        >
          <LabelOptions labels={CRM_REVENUE_KIND_LABELS} />
        </Select>
        <Input
          placeholder="Libellé (ex : abonnement août)"
          aria-label="Libellé de la ligne"
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
        />
        <Input
          type="date"
          aria-label="Début de période"
          title="Début de période"
          value={draft.periodStart}
          onChange={(e) => setDraft({ ...draft, periodStart: e.target.value })}
        />
        <Input
          placeholder="Montant $"
          aria-label="Montant en dollars"
          value={draft.amount}
          onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
        />
        <Select
          aria-label="Statut de la ligne"
          value={draft.status}
          onChange={(e) => setDraft({ ...draft, status: e.target.value as CrmRevenueStatus })}
        >
          <LabelOptions labels={CRM_REVENUE_STATUS_LABELS} />
        </Select>
        {editingId ? (
          <>
            <Input
              type="date"
              aria-label="Fin de période"
              title="Fin de période"
              value={draft.periodEnd}
              onChange={(e) => setDraft({ ...draft, periodEnd: e.target.value })}
            />
            <Input
              type="number"
              min={0}
              max={100}
              step={0.5}
              placeholder={`Commission % (dossier : ${commissionPct} %)`}
              aria-label="Taux de commission de la ligne"
              value={draft.commissionPct}
              onChange={(e) => setDraft({ ...draft, commissionPct: e.target.value })}
            />
            <Input
              placeholder="Référence de facture"
              aria-label="Référence de facture"
              value={draft.invoiceRef}
              onChange={(e) => setDraft({ ...draft, invoiceRef: e.target.value })}
            />
            <Input
              type="date"
              aria-label="Encaissé le"
              title="Encaissé le"
              value={draft.paidAt}
              onChange={(e) => setDraft({ ...draft, paidAt: e.target.value })}
            />
            <Input
              placeholder="Notes"
              aria-label="Notes de la ligne"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </>
        ) : null}
        <div className="crm-form__actions">
          {editingId ? (
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              Annuler
            </Button>
          ) : null}
          <Button type="submit" size="sm" disabled={saving || !draft.label.trim()}>
            {editingId ? "Enregistrer" : "Ajouter"}
          </Button>
        </div>
      </form>

      <table className="table">
        <thead>
          <tr>
            <th>Période</th>
            <th>Ligne</th>
            <th>Montant</th>
            <th>Commission</th>
            <th>Facture</th>
            <th>Statut</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={7} className="table__empty">
                Aucun revenu — ajoutez la première ligne encaissée ou prévue.
              </td>
            </tr>
          ) : (
            items.map((r) => (
              <tr key={r.id}>
                <td title={r.paidAt ? `Encaissé le ${formatDateTime(r.paidAt)}` : undefined}>
                  {formatDateTime(r.periodStart, { dateStyle: "medium" })}
                  {r.periodEnd ? ` → ${formatDateTime(r.periodEnd, { dateStyle: "medium" })}` : ""}
                </td>
                <td title={r.notes ?? undefined}>
                  {r.label}{" "}
                  <span className="crm-chip crm-chip--muted">
                    {CRM_REVENUE_KIND_LABELS[r.kind]}
                  </span>
                </td>
                <td>
                  <span className={`crm-money${r.amountCents < 0 ? " crm-money--neg" : ""}`}>
                    {formatCents(r.amountCents, r.currency)}
                  </span>
                </td>
                <td title={`Taux appliqué : ${r.commissionPct ?? commissionPct} %`}>
                  <span className={`crm-money${r.commissionCents < 0 ? " crm-money--neg" : ""}`}>
                    {formatCents(r.commissionCents, r.currency)}
                  </span>
                </td>
                <td>{r.invoiceRef ?? "—"}</td>
                <td>
                  <Select
                    aria-label={`Statut de la ligne ${r.label}`}
                    value={r.status}
                    onChange={(e) => void changeStatus(r.id, e.target.value as CrmRevenueStatus)}
                  >
                    <LabelOptions labels={CRM_REVENUE_STATUS_LABELS} />
                  </Select>
                </td>
                <td className="table__actions">
                  <Button variant="ghost" size="sm" onClick={() => edit(r)}>
                    Modifier
                  </Button>
                  <Button variant="ghost-danger" size="sm" onClick={() => void remove(r.id)}>
                    Supprimer
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}
